(function () {
    'use strict';

    angular
        .module('app')
        .factory('NavigationAidService', NavigationAidService);

    NavigationAidService.$inject = ['$rootScope', '$location', '$window', '$timeout', 'UnsavedChangesService'];

    /**
     * @ngdoc service
     * @name NavigationAidService
     * 
     * @description
     * Service for going 'back' in navigation history. Useful on pages where user has navigated to an edit screen.
     * After the user clicks, e.g. 'Save' on that edit screen, it might make sense to 'go back' to the last screen,
     * by using the 'goBack' function.
     */
    function NavigationAidService($rootScope, $location, $window, $timeout, UnsavedChangesService) {
        var service = {
            pathHistory: [],
            goBack: goBack,
            goToIndex: goToIndex,
            setTitle: setTitle,
            setCurrentTabPage: setCurrentTabPage,
            getCurrentTabPage: getCurrentTabPage,
            getPreviousPath: getPreviousPath,
            watchTabPage: watchTabPage,
            replacePage: replacePage
        };

        var pathHistoryLengthLimit = 20;
        var dashboardPath = '/dashboard';
        var dashboardTitle = 'Dashboard';

        activate();

        return service;

        function activate() {
            $rootScope.$on('$routeChangeSuccess', function (event, currentRoute, previousRoute) {

                // Ignore "redirect" routes
                if (_.isNil(currentRoute.$$route) || !_.isNil(currentRoute.$$route.redirectTo)) {
                    return;
                }

                if (!_.isNil($window.history.state) && !_.isNil($window.history.state.pathHistory) && $window.history.state.path === $location.url()) {

                    // If history state includes a pathHistory object, re-use it.
                    // This helps preserve the correct path history when the user uses back and forward buttons,
                    // or refreshes the page.

                    // state.path is compared to $location.url because IE11 and Edge 14 sometimes load the *wrong* history state!
                    // Not sure whether this is a bug with history.state in those browsers, or something to do with $routeChangeSuccess.

                    service.pathHistory = $window.history.state.pathHistory;
                } else {

                    // If navigating to a "root" navigation item, clear the history
                    if (currentRoute.$$route && currentRoute.$$route.rootNavigationItem) {
                        service.pathHistory.length = 0;
                    }

                    saveCurrentPathToHistory();
                }
            });
        }

        /**
         * A path URL may have a trailing slash that needs to be ignored in comparisons.
         * Also, we're not concerned about path case sensitivity.
         * We have to cut the trailing slash because angular routing will re-route to a path *without* a trailing slash,
         * which could cause duplicate entries in path history otherwise.
         *
         * @param {string} path - A URL path for formatting.
         * @returns {string} A URL formatted for consistent comparison, with trailing slash removed and in lower case. 
         */
        function formatPathForComparison(path) {
            return path.replace(/\/+$/, "").toLowerCase();
        }

        /**
         * Save the current path to the end of navigation history.
         */
        function saveCurrentPathToHistory() {
            var path = $location.url();

            if (service.pathHistory.length > 0) {
                var lastPathEntry = formatPathForComparison(service.pathHistory[service.pathHistory.length - 1].path);
                var comparison = formatPathForComparison(path);

                // If the last entry is the same as the one being added, ignore the request to add it to history
                if (lastPathEntry === comparison) {
                    return;
                }
            }

            if (service.pathHistory.length === 0 && formatPathForComparison(dashboardPath) !== formatPathForComparison(path)) {
                // If history is empty, and this is *not* the dashboard, put the dashboard first in history.
                service.pathHistory.push({
                    path: dashboardPath,
                    title: dashboardTitle
                });
            }

            service.pathHistory.push({
                path: path,
                title: null
            });

            if (service.pathHistory.length > pathHistoryLengthLimit) {
                service.pathHistory = service.pathHistory.slice(service.pathHistory.length - pathHistoryLengthLimit);
            }

            saveHistoryState();
        }

        /**
         * Go back one position in history.
         */
        function goBack() {
            goToIndex(service.pathHistory.length - 2);
        }

        /**
         * Clear history from the specified index onwards.
         *
         * @param {number} index - Zero-based index in path history. If null
         * or undefined, will clear all history
         */
        function clearHistory(index) {
            if (_.isNil(index)) {
                service.pathHistory.length = 0;
            } else if (service.pathHistory.length > index) {
                service.pathHistory.splice(index, service.pathHistory.length - index);
            }
        }

        /**
         * Navigate to the specified point in the path history.
         *
         * @param {number} index - Zero-based index in path history.
         */
        function goToIndex(index) {
            if (service.pathHistory.length > index && index >= 0) {
                UnsavedChangesService.promptUnsavedChanges().then(function () {

                    // Use timeout, otherwise you'll get an infinite digest
                    $timeout(function () {
                        var destination = service.pathHistory[index];

                        // Skip dirty check to ensure navigation can proceed
                        UnsavedChangesService.skipDirtyCheck = true;

                        clearHistory(index + 1);
                        $window.history.pushState({ pathHistory: service.pathHistory }, destination.title, '/#' + destination.path);
                    });
                });
            }
        }

        /**
         * Set the title for the current path history entry.
         *
         * @param {string} title
         */
        function setTitle(title) {
            if (service.pathHistory.length > 0) {
                service.pathHistory[service.pathHistory.length - 1].title = title;
                saveHistoryState();
            }
        }

        /**
         * Set the tab page for the current path history entry.
         *
         * @param {string} tabPage
         */
        function setCurrentTabPage(tabPage) {
            if (service.pathHistory.length > 0) {
                service.pathHistory[service.pathHistory.length - 1].currentTabPage = tabPage;
                saveHistoryState();
            }
        }

        /**
         * Get the tab page from the current path history entry.
         *
         * @returns {string} May be null if there is no current tab page defined.
         */
        function getCurrentTabPage() {
            if (service.pathHistory.length > 0) {
                var currentTab = service.pathHistory[service.pathHistory.length -1].currentTabPage;
                if (!_.isNil(currentTab)) {
                    return currentTab;
                }
            }
            return null;
        }

        /**
         * Get the previous path from the path history.
         *
         * @returns {string} May be null if there is no previous path defined.
         */
        function getPreviousPath() {
            if (service.pathHistory.length > 1) {
                var previousPath = service.pathHistory[service.pathHistory.length - 2].path;
                if (!_.isNil(previousPath)) {
                    return previousPath;
                }
            }
            return null;
        }

        /**
         * Watch for changes to tab page selection, and save to history.
         *
         * @param {object} tabScope - AngularJS scope object that contains the variable holding the current tab name.
         * @param {string} tabVariableName - Name of variable in 'tabScope', e.g. 'vm.currentTabPage'.
         */
        function watchTabPage(tabScope, tabVariableName) {
            tabScope.$watch(tabVariableName, function (newTabPage, oldTabPage) {
                if (newTabPage !== oldTabPage) {
                    // Save tab page selection to history
                    setCurrentTabPage(newTabPage);
                }
            });
        }

        /**
         * Replace the current page with the new one.
         *
         * @param {string} newUrl
         */
        function replacePage(newUrl) {
            if (service.pathHistory.length > 0) {
                service.pathHistory.length = service.pathHistory.length - 1;
            }

            $location.url(newUrl).replace();
        }

        /**
         * Save state to the JS history API "state" property.
         */
        function saveHistoryState() {

            // Replace state in a $timeout to ensure it happens during a digest.
            // If state is replaced outside of digest, the next digest will consider
            // it to have been an "out of Angular" change to URL, and will trigger
            // a $locationChangeStart event which could erroneously re-load controllers, etc.

            $timeout(function () {

                if (service.pathHistory.length > 0) {
                    var currentHistory = service.pathHistory[service.pathHistory.length - 1];

                    // Only replace the state if the current location is the same as specified in path history.
                    // It *may* differ if this function is called by a $timeout.
                    if (currentHistory.path == $location.url()) {
                        var state = {
                            pathHistory: service.pathHistory,
                            path: currentHistory.path
                        };

                        $window.history.replaceState(state, currentHistory.title, '/#' + currentHistory.path);
                    }
                }
            });
        }
    }
})();